import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useCallback, useContext, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { CheckmarkMedium, Icon20 } from "@/icons/Icons.ts"
import { getLogger } from "@/lib/logging.ts"
import { InviteTeammatesDialog } from "@/routes/auth/InviteTeammatesDialog.tsx"
import { useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"
import { TaskViewModelContext } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import { ellipsis } from "@/styles/mixins.ts"
import { baseFontSize } from "@/styles/textStyles.ts"

import type { AiChatMessageContent } from "@/lib/client/generated/TypeDefinitions"


const log = getLogger("PreviewReadyMessage")

export const PreviewReadyMessage = observer((props: {
  content: AiChatMessageContent.InitProject.PreviewReady
  isActive: boolean
}) => {
  const taskModel = useContext(TaskViewModelContext)
  const isAllSetUpAndReady = taskModel?.isFinishedConfigurationTask?.get()
  const isCompleting = taskModel?.completingPreviewRuntimeSetup?.get() === true

  if (isAllSetUpAndReady && !isCompleting) {
    return taskModel?.showPreviewLooksGoodButton ? <AllSetAndReadyMessage isActive={props.isActive} /> : <></>
  } else {
    return <CheckPreviewMessage isActive={props.isActive} />
  }
})

const CheckPreviewMessage = observer((props: {
  isActive: boolean
}) => {
  const taskModel = useContext(TaskViewModelContext)
  const [completeInProgress, setCompleteInProgress] = useState(false)

  const previewModel = taskModel?.currentPreview?.get()
  const isPortListening = previewModel
    ? previewModel?.portModel?.isPortBeingListened.get() ?? false
    : false

  const approveButtonDisabled = !isPortListening || completeInProgress

  const handleApproveButtonClick = useCallback(async () => {
    setCompleteInProgress(true)
    try {
      await taskModel?.completePreviewRuntimeSetup()
    } catch (e) {
      log.error("Failed to complete preview runtime setup", e)
    } finally {
      setCompleteInProgress(false)
    }
  }, [taskModel, setCompleteInProgress])

  const button = (
    <ApproveButton
      kind="alternative"
      onClick={handleApproveButtonClick}
      disabled={approveButtonDisabled}
      data-testid="finish-setup-button"
    >
      <CheckmarkMedium className={Icon20} color="var(--success-color)" />
      Preview looks good!
    </ApproveButton>
  )

  return (
    <AgentMessageContainer isActive={props.isActive}>
      <CheckPreviewPill>
        <Content>
          <Title>
            Check the preview
          </Title>
          <Subtitle>
            If everything looks good, mark the preview as ready to finish setup.<br />
            After that, this setup becomes read-only and you can start new ideas.
          </Subtitle>
        </Content>
        <Horizontal alignItems="center" gap="16px">
          {isPortListening ? (
            <>
              {button}
              <ApproveButtonSideText title="If not, continue in the chat to fix it">
                If not, continue in the chat to fix it
              </ApproveButtonSideText>
            </>
          ) : (
            <Tooltip content="Connect to a valid port to continue" align="start">
              {button}
            </Tooltip>
          )}
        </Horizontal>
      </CheckPreviewPill>
    </AgentMessageContainer>
  )
})

const AllSetAndReadyMessage = observer((props: {
  isActive: boolean
}) => {
  const taskModel = useContext(TaskViewModelContext)
  const permissions = useOrganizationPermissions()

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)

  return (
    <AgentMessageContainer isActive={props.isActive}>
      <AllSetAndReadyPill>
        <Content>
          <Title>
            All set and ready!
          </Title>
          <span>
            The setup is complete. The chat is now read-only and can be accessed from your project settings.
            Start collaborating and bring your ideas to life!
          </span>
        </Content>
        <Horizontal alignItems="center" gap="8px">
          <CreateNewIdeaButton
            kind="primary"
            onClick={() => taskModel?.navigateFromCompletedOnboarding()}
            disabled={!taskModel}
            data-testid="create-new-idea-button"
          >
            Create new idea
          </CreateNewIdeaButton>

          {permissions.canInviteUsers && (
            <>
              <InviteTeammateButton
                kind="alternative"
                onClick={() => setInviteDialogOpen(true)}
                disabled={!taskModel}
                data-testid="invite-teammate-button"
              >
                Invite teammate
              </InviteTeammateButton>

              <InviteTeammatesDialog
                dialogOpen={inviteDialogOpen}
                setDialogOpen={setInviteDialogOpen}
              />
            </>
          )}
        </Horizontal>
      </AllSetAndReadyPill>
    </AgentMessageContainer>
  )
})

const Content = styled(Vertical)`
  gap: 4px;
  padding-bottom: 12px;
`

const Pill = styled(Vertical)`
  border-radius: var(--border-radius-20);
  padding: 16px;
`

const AllSetAndReadyPill = styled(Pill)`
  background: linear-gradient(0deg, rgb(61 80 255 / 10%), rgb(61 80 255 / 10%)),
              linear-gradient(82.83deg, rgb(235 224 252 / 0%) 38.74%, #EBE0FC 94.8%);
`

const CheckPreviewPill = styled(Pill)`
  background: radial-gradient(47.88% 156.96% at 12.36% 100%, rgb(96 255 28 / 20%) 20%, rgb(12 238 110 / 0%) 100%),
              linear-gradient(0deg, #EEF, #EEF);
`

const Title = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`

const Subtitle = styled.span`
  ${baseFontSize};

  font-weight: 400;
`

const ApproveButton = styled(Button)`
  gap: 4px;

  height: 40px;

  flex-shrink: 1;
  flex-grow: 0;

  padding-left: 10px;

  color: oklch(60.29% 0.153 162.32deg);
  background: radial-gradient(
    44.33% 100% at 51.79% 100%,
    oklch(96.5% 0.048 155deg) 0%,
    oklch(99% 0.012 140deg) 100%
  );
  border: none;

  &:hover:not(:disabled) {
    color: oklch(60.29% 0.153 162.32deg);
    background: radial-gradient(
      44.33% 100% at 51.79% 100%,
      oklch(95% 0.055 155deg) 0%,
      oklch(98% 0.012 140deg) 100%
    );
  }

  font-weight: 500;
`

const CreateNewIdeaButton = styled(Button)`
  height: 40px;
  font-size: var(--base-font-size);
`

const InviteTeammateButton = styled(CreateNewIdeaButton)`
  background: transparent;
  border: 1px solid rgb(58 58 59 / 20%);
`

const ApproveButtonSideText = styled.span`
  ${baseFontSize};

  color: var(--secondary-text-color);
  font-weight: 400;

  ${ellipsis};
`
